import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  Search,
  Link2,
  Network,
  X,
  Trash2,
  RefreshCw,
  Globe,
  ExternalLink,
} from 'lucide-react';
import { api, handleApiError } from '../services/client';
import { skillService } from '../services/skills';
import { tabService } from '../services/tabs';
import { toast } from 'sonner';
import { WS_SESSION_STORAGE_KEY } from '@/context/TabMonitorSessionContext';
import { categorizeTabUrl, CATEGORY_GRAPH_COLOR, type TabCategory } from '@/utils/tabCategories';
import {
  layoutRelationshipGraph,
  computeGraphViewBox,
  edgePath,
  truncateLabel,
  type LayoutGroup,
} from '@/utils/relationshipGraphLayout';
const GRAPH_CACHE_KEY = 'khiimori.relationshipGraph.v2';

async function resolveWorkspaceSessionId(): Promise<string | null> {
  const cached = sessionStorage.getItem(WS_SESSION_STORAGE_KEY);
  if (cached) return cached;
  try {
    const cur = await api.get<{ session: { id: string } }>('/sessions/current');
    const id = cur.data.session?.id;
    if (id) {
      sessionStorage.setItem(WS_SESSION_STORAGE_KEY, id);
      return id;
    }
  } catch {
    // fall through
  }
  try {
    const list = await api.get<{ sessions: { id: string }[] }>('/sessions');
    const first = list.data.sessions?.[0]?.id;
    if (first) {
      sessionStorage.setItem(WS_SESSION_STORAGE_KEY, first);
      return first;
    }
  } catch {
    // ignore
  }
  return null;
}

function applyLayout(entities: Entity[], groups: RelationshipGroup[]) {
  const input = entities.map((e) => ({
    ...e,
    type: e.type,
    cluster: String(e.data?.cluster ?? ''),
    host: String(e.data?.host ?? ''),
  }));
  const layoutGroups: LayoutGroup[] = groups.map((g) => ({
    id: g.id,
    name: g.name,
    color: g.color,
    entities: g.entities,
  }));
  return layoutRelationshipGraph(input, layoutGroups);
}

function mapEdgeType(label?: string): Connection['type'] {
  const l = (label ?? '').toLowerCase();
  if (l.includes('duplicate')) return 'similar';
  if (l.includes('same site')) return 'similar';
  if (l.includes('shared')) return 'related';
  if (l.includes('co-occurrence') || l.includes('mentioned')) return 'related';
  if (l.includes('cluster')) return 'related';
  return 'related';
}

function mapGraphPayload(data: Record<string, unknown>) {
  const rawNodes = Array.isArray(data.nodes) ? data.nodes : [];
  const rawEdges = Array.isArray(data.edges) ? data.edges : [];
  const rawGroups = Array.isArray(data.groups) ? data.groups : [];

  const entities: Entity[] = rawNodes.map((n: Record<string, unknown>) => ({
    id: String(n.id ?? ''),
    name: String(n.name ?? n.label ?? 'Tab'),
    type: (String(n.type ?? 'tab') as Entity['type']),
    color: String(n.color ?? '#8b5cf6'),
    x: 0,
    y: 0,
    size: String(n.type ?? 'tab') === 'tab' ? 22 : 18,
    width: 210,
    height: 54,
    cluster: String((n.data as Record<string, unknown>)?.cluster ?? n.type ?? 'general'),
    host: String((n.data as Record<string, unknown>)?.host ?? ''),
    data: (n.data as Record<string, unknown>) ?? {},
  }));
  const connections: Connection[] = rawEdges.map((e: Record<string, unknown>, i: number) => ({
    id: String(e.id ?? `edge-${i}`),
    sourceId: String(e.sourceId ?? e.source ?? ''),
    targetId: String(e.targetId ?? e.target ?? ''),
    type: mapEdgeType(String(e.label ?? e.type ?? '')),
    strength: Number(e.strength ?? 0.7),
    label: e.label ? String(e.label) : undefined,
  }));

  const groups: RelationshipGroup[] = rawGroups.map((g: Record<string, unknown>) => ({
    id: String(g.id ?? ''),
    name: String(g.name ?? 'Cluster'),
    color: String(g.color ?? '#8b5cf6'),
    entities: Array.isArray(g.entities) ? g.entities.map(String) : [],
  }));

  return { entities, connections, groups };
}

async function enrichGraphFromSessionTabs(
  sessionId: string,
  mapped: ReturnType<typeof mapGraphPayload>,
): Promise<ReturnType<typeof mapGraphPayload>> {
  try {
    const { tabs } = await tabService.getTabsBySession(sessionId, 1, 100);
    const tabById = new Map(tabs.map((t) => [t.id, t]));
    const entities = mapped.entities.map((e) => {
      const tab = tabById.get(e.id);
      if (!tab?.url) return e;
      let host = '';
      try {
        host = new URL(tab.url).hostname.replace(/^www\./, '');
      } catch {
        host = '';
      }
      const category = categorizeTabUrl(tab.url) as TabCategory;
      return {
        ...e,
        name: tab.title?.trim() || e.name,
        host,
        cluster: category,
        color: CATEGORY_GRAPH_COLOR[category] ?? e.color,
        data: { ...e.data, url: tab.url, host, cluster: category },
      };
    });
    return { ...mapped, entities };
  } catch {
    return mapped;
  }
}

async function loadSessionRelationshipGraph(sessionId: string): Promise<ReturnType<typeof mapGraphPayload> | null> {
  try {
    const { data } = await api.get<{ nodes: Record<string, unknown>[]; edges: Record<string, unknown>[] }>(
      `/sessions/${sessionId}/tabs/relationship-graph`,
    );
    const nodes = Array.isArray(data.nodes) ? data.nodes : [];
    if (nodes.length === 0) return null;
    const mapped = mapGraphPayload({
      nodes,
      edges: Array.isArray(data.edges) ? data.edges : [],
      groups: [],
    });
    return enrichGraphFromSessionTabs(sessionId, mapped);
  } catch {
    return null;
  }
}

async function loadRelationshipGraph(sessionId?: string | null): Promise<ReturnType<typeof mapGraphPayload>> {
  try {
    const cached = sessionStorage.getItem(GRAPH_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached) as Record<string, unknown>;
      if (Array.isArray(parsed.nodes) && parsed.nodes.length > 0) {
        let mapped = mapGraphPayload(parsed);
        if (sessionId) {
          mapped = await enrichGraphFromSessionTabs(sessionId, mapped);
        }
        const laidOut = applyLayout(mapped.entities, mapped.groups);
        return { ...mapped, entities: laidOut.entities, graphWidth: laidOut.width, graphHeight: laidOut.height };
      }
    }
  } catch {
    // ignore bad cache
  }

  const params: Record<string, unknown> = {};
  if (sessionId) params.session_id = sessionId;

  try {
    const res = await skillService.executeSkill('build_relationship_map', params);
    if (res.status === 'success' && res.data) {
      let mapped = mapGraphPayload(res.data);
      if (sessionId) {
        mapped = await enrichGraphFromSessionTabs(sessionId, mapped);
      }
      const laidOut = applyLayout(mapped.entities, mapped.groups);
      const result = { ...mapped, entities: laidOut.entities, graphWidth: laidOut.width, graphHeight: laidOut.height };
      if (result.entities.length > 0) {
        try {
          sessionStorage.setItem(GRAPH_CACHE_KEY, JSON.stringify(res.data));
        } catch {
          // ignore
        }
        return result;
      }
    }
  } catch {
    // fall through to session graph
  }

  if (sessionId) {
    const sessionGraph = await loadSessionRelationshipGraph(sessionId);
    if (sessionGraph) {
      const laidOut = applyLayout(sessionGraph.entities, sessionGraph.groups);
      return { ...sessionGraph, entities: laidOut.entities, graphWidth: laidOut.width, graphHeight: laidOut.height };
    }
  }

  throw new Error('No tabs found to build a relationship map. Sync tabs from the extension, then click Rebuild.');
}
interface Entity {
  id: string;
  name: string;
  type: 'tab' | 'session' | 'bookmark' | 'workspace' | 'research' | 'person' | 'tag' | 'location' | 'organization' | 'date';
  color: string;
  x: number;
  y: number;
  size: number;
  width?: number;
  height?: number;
  cluster: string;
  host: string;
  data?: Record<string, unknown>;
}
interface Connection {
  id: string;
  sourceId: string;
  targetId: string;
  type: 'reference' | 'related' | 'parent' | 'child' | 'similar';
  strength: number;
  label?: string;
}

interface RelationshipGroup {
  id: string;
  name: string;
  color: string;
  entities: string[];
}

// Sample data — kept as fallback only; real data comes from the API
const sampleEntities: Entity[] = [];
const sampleConnections: Connection[] = [];
const sampleGroups: RelationshipGroup[] = [];

export const RelationshipMapPage: React.FC = () => {
  // State
  const [entities, setEntities] = useState<Entity[]>(sampleEntities);
  const [connections, setConnections] = useState<Connection[]>(sampleConnections);
  const [groups, setGroups] = useState<RelationshipGroup[]>(sampleGroups);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPan, setLastPan] = useState({ x: 0, y: 0 });
  const [showConnections, setShowConnections] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [connectionMode, setConnectionMode] = useState(false);
  const [connectionSource, setConnectionSource] = useState<string | null>(null);
  const [hoveredEntity, setHoveredEntity] = useState<string | null>(null);
  const [hoveredConnection, setHoveredConnection] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [graphWidth, setGraphWidth] = useState(960);
  const [graphHeight, setGraphHeight] = useState(640);
  const [activeCluster, setActiveCluster] = useState<string | null>(null);

  const commitGraph = useCallback((mapped: ReturnType<typeof mapGraphPayload>) => {
    const laidOut = applyLayout(mapped.entities, mapped.groups);
    setEntities(laidOut.entities as Entity[]);
    setConnections(mapped.connections);
    setGroups(mapped.groups);
    setGraphWidth(laidOut.width);
    setGraphHeight(laidOut.height);
    setPan({ x: 0, y: 0 });
    setZoom(1);
  }, []);

  const refreshGraph = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      sessionStorage.removeItem(GRAPH_CACHE_KEY);
      const sessionId = await resolveWorkspaceSessionId();
      const params: Record<string, unknown> = {};
      if (sessionId) params.session_id = sessionId;

      let mapped: ReturnType<typeof mapGraphPayload> | null = null;
      let message = 'Relationship map rebuilt.';

      try {
        const res = await skillService.executeSkill('build_relationship_map', params);
        if (res.status === 'success' && res.data) {
          mapped = mapGraphPayload(res.data);
          message = res.message || message;
          if (mapped.entities.length > 0) {
            sessionStorage.setItem(GRAPH_CACHE_KEY, JSON.stringify(res.data));
          }
        }
      } catch {
        // try session fallback below
      }

      if (!mapped || mapped.entities.length === 0) {
        if (sessionId) {
          const sessionGraph = await loadSessionRelationshipGraph(sessionId);
          if (sessionGraph) {
            mapped = sessionGraph;
            message = `Mapped ${sessionGraph.entities.length} tab(s) from workspace session.`;
          }
        }
      } else if (sessionId) {
        mapped = await enrichGraphFromSessionTabs(sessionId, mapped);
      }

      if (!mapped || mapped.entities.length === 0) {
        throw new Error('No tabs found to map. Sync tabs from the extension first.');
      }

      commitGraph(mapped);
      toast.success(message);
    } catch (err) {
      setError(handleApiError(err));
      setEntities([]);
      setConnections([]);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [commitGraph]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const sessionId = await resolveWorkspaceSessionId();
        const mapped = await loadRelationshipGraph(sessionId);
        if (!cancelled) {
          setEntities(mapped.entities);
          setConnections(mapped.connections);
          setGroups(mapped.groups);
          setGraphWidth(mapped.graphWidth ?? 960);
          setGraphHeight(mapped.graphHeight ?? 640);
        }
      } catch (err) {
        if (!cancelled) {
          setError(handleApiError(err));
          setEntities([]);
          setConnections([]);
          setGroups([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const graphViewBox = useMemo(
    () => computeGraphViewBox(entities, graphWidth, graphHeight),
    [entities, graphWidth, graphHeight],
  );

  const clusterChips = useMemo(() => {
    const chips = new Map<string, { label: string; color: string; count: number }>();
    for (const e of entities) {
      const label = e.cluster || 'General';
      const existing = chips.get(label);
      if (existing) {
        existing.count += 1;
      } else {
        chips.set(label, { label, color: e.color, count: 1 });
      }
    }
    return [...chips.values()];
  }, [entities]);

  const columnHeaders = useMemo(() => {
    const headers: { x: number; label: string; color: string; count: number }[] = [];
    const seen = new Set<string>();
    for (const e of entities) {
      const colX = e.data?._colX as number | undefined;
      const label = (e.data?._colHeader as string) ?? e.cluster;
      if (colX == null || seen.has(label)) continue;
      seen.add(label);
      headers.push({
        x: colX + (e.width ?? 210) / 2,
        label,
        color: (e.data?._colAccent as string) ?? e.color,
        count: (e.data?._colCount as number) ?? 1,
      });
    }
    return headers;
  }, [entities]);

  const connectionLegend = [
    { type: 'similar' as const, label: 'Same site', color: '#f472b6' },
    { type: 'related' as const, label: 'Related topic', color: '#34d399' },
    { type: 'reference' as const, label: 'Reference', color: '#a78bfa' },
  ];

  const getConnectionColor = (type: Connection['type'], label?: string) => {
    const l = (label ?? '').toLowerCase();
    if (l.includes('same site') || l.includes('duplicate')) return '#f472b6';
    if (l.includes('cluster')) return '#34d399';
    switch (type) {
      case 'reference': return '#a78bfa';
      case 'related': return '#34d399';
      case 'parent': return '#fbbf24';
      case 'child': return '#60a5fa';
      case 'similar': return '#f472b6';
      default: return '#64748b';
    }
  };

  const filteredEntities = useMemo(() => {
    let result = entities;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (e) =>
          e.name.toLowerCase().includes(query) ||
          e.host.toLowerCase().includes(query) ||
          e.cluster.toLowerCase().includes(query),
      );
    }

    if (activeCluster) {
      result = result.filter((e) => e.cluster === activeCluster);
    }

    return result;
  }, [entities, searchQuery, activeCluster]);

  const visibleIds = useMemo(() => new Set(filteredEntities.map((e) => e.id)), [filteredEntities]);

  const highlightedIds = useMemo(() => {
    if (!selectedEntity && !hoveredEntity) return null;
    const focusId = selectedEntity?.id ?? hoveredEntity;
    if (!focusId) return null;
    const ids = new Set<string>([focusId]);
    for (const conn of connections) {
      if (conn.sourceId === focusId) ids.add(conn.targetId);
      if (conn.targetId === focusId) ids.add(conn.sourceId);
    }
    return ids;
  }, [selectedEntity, hoveredEntity, connections]);

  // Handle mouse events for panning
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0 && !connectionMode) {
      setIsPanning(true);
      setLastPan({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      setPan({ x: e.clientX - lastPan.x, y: e.clientY - lastPan.y });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  // Zoom controls
  const zoomIn = () => setZoom(Math.min(zoom * 1.2, 3));
  const zoomOut = () => setZoom(Math.max(zoom / 1.2, 0.3));
  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const exitConnectionMode = useCallback(() => {
    setConnectionMode(false);
    setConnectionSource(null);
  }, []);

  const toggleConnectionMode = useCallback(() => {
    setConnectionMode((prev) => {
      if (prev) setConnectionSource(null);
      return !prev;
    });
  }, []);

  // Handle entity click — stopPropagation on the node rect so the pan layer does not swallow it.
  const handleEntityClick = useCallback((entity: Entity, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (connectionMode) {
      if (!connectionSource) {
        setConnectionSource(entity.id);
        setSelectedEntity(null);
        return;
      }
      if (connectionSource === entity.id) return;

      const alreadyLinked = connections.some(
        (c) =>
          (c.sourceId === connectionSource && c.targetId === entity.id) ||
          (c.sourceId === entity.id && c.targetId === connectionSource),
      );
      if (alreadyLinked) {
        toast.info('These tabs are already connected');
        exitConnectionMode();
        return;
      }

      const newConnection: Connection = {
        id: `manual-${connectionSource}-${entity.id}`,
        sourceId: connectionSource,
        targetId: entity.id,
        type: 'reference',
        strength: 0.85,
        label: 'Manual link',
      };
      setConnections((prev) => [...prev, newConnection]);
      exitConnectionMode();
      toast.success('Connection created');
      return;
    }

    setSelectedEntity(entity);
  }, [connectionMode, connectionSource, connections, exitConnectionMode]);

  // Delete entity
  const deleteEntity = (entityId: string) => {
    setEntities(entities.filter(e => e.id !== entityId));
    setConnections(connections.filter(c => c.sourceId !== entityId && c.targetId !== entityId));
    if (selectedEntity?.id === entityId) {
      setSelectedEntity(null);
    }
  };

  // Delete connection
  const deleteConnection = (connectionId: string) => {
    setConnections(connections.filter(c => c.id !== connectionId));
  };

  // Get connected entities
  return (
    <div className="flex flex-col bg-khiimori-bg-secondary min-h-[calc(100vh-14rem)] rounded-xl border border-khiimori-border overflow-hidden">
      {/* Header */}
      <div className="border-b border-khiimori-border bg-khiimori-bg-elevated/40 p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-khiimori-text-primary">Relationship Map</h1>
            <p className="text-sm text-khiimori-text-muted mt-1">
              Tabs grouped by topic and site — click a node to explore connections
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void refreshGraph()}
              disabled={loading}
              className="px-4 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors flex items-center gap-2 disabled:opacity-50 text-sm font-medium"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Rebuild
            </button>
            <button
              onClick={toggleConnectionMode}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm ${
                connectionMode
                  ? 'bg-violet-600 text-white'
                  : 'bg-khiimori-bg-elevated text-khiimori-text-secondary hover:bg-khiimori-bg-tertiary border border-khiimori-border'
              }`}
            >
              <Link2 size={16} />
              {connectionMode ? 'Connecting…' : 'Connect'}
            </button>
          </div>
        </div>

        {/* Summary stats */}
        {entities.length > 0 && (
          <div className="mt-4 grid grid-cols-3 gap-3 max-w-lg">
            {[
              { label: 'Tabs mapped', value: entities.length, color: '#a78bfa' },
              { label: 'Connections', value: connections.length, color: '#34d399' },
              { label: 'Topic groups', value: clusterChips.length, color: '#60a5fa' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-lg border border-khiimori-border bg-khiimori-bg-secondary/80 px-3 py-2"
              >
                <p className="text-[10px] uppercase tracking-wide text-khiimori-text-muted">{stat.label}</p>
                <p className="text-lg font-semibold" style={{ color: stat.color }}>
                  {stat.value}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Toolbar */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-khiimori-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tabs, sites, topics…"
              className="w-full pl-10 pr-4 py-2 bg-khiimori-bg-secondary border border-khiimori-border rounded-lg text-sm text-khiimori-text-primary placeholder-khiimori-text-muted focus:outline-none focus:border-violet-500"
            />
          </div>

          <div className="flex items-center gap-1 rounded-lg border border-khiimori-border bg-khiimori-bg-secondary p-1">
            <button onClick={zoomOut} className="p-2 rounded-md hover:bg-khiimori-bg-tertiary text-khiimori-text-secondary" title="Zoom out">
              <ZoomOut size={16} />
            </button>
            <span className="text-xs text-khiimori-text-muted min-w-[48px] text-center">{Math.round(zoom * 100)}%</span>
            <button onClick={zoomIn} className="p-2 rounded-md hover:bg-khiimori-bg-tertiary text-khiimori-text-secondary" title="Zoom in">
              <ZoomIn size={16} />
            </button>
            <button onClick={resetView} className="p-2 rounded-md hover:bg-khiimori-bg-tertiary text-khiimori-text-secondary" title="Reset view">
              <Maximize2 size={16} />
            </button>
          </div>

          <button
            onClick={() => setShowConnections(!showConnections)}
            className={`p-2 rounded-lg border transition-colors ${
              showConnections
                ? 'bg-violet-500/20 border-violet-500/40 text-violet-300'
                : 'bg-khiimori-bg-secondary border-khiimori-border text-khiimori-text-muted'
            }`}
            title="Toggle connections"
          >
            <Link2 size={16} />
          </button>
        </div>

        {/* Cluster filters */}
        {clusterChips.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-khiimori-text-muted mr-1">Topics:</span>
            <button
              onClick={() => setActiveCluster(null)}
              className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                activeCluster === null
                  ? 'bg-violet-500/20 border-violet-500/50 text-violet-200'
                  : 'border-khiimori-border text-khiimori-text-muted hover:border-khiimori-text-muted'
              }`}
            >
              All
            </button>
            {clusterChips.map((chip) => (
              <button
                key={chip.label}
                onClick={() => setActiveCluster(activeCluster === chip.label ? null : chip.label)}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors flex items-center gap-1.5 ${
                  activeCluster === chip.label
                    ? 'text-white border-transparent'
                    : 'border-khiimori-border text-khiimori-text-muted hover:border-khiimori-text-muted'
                }`}
                style={
                  activeCluster === chip.label
                    ? { backgroundColor: chip.color, borderColor: chip.color }
                    : undefined
                }
              >
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: chip.color }} />
                {chip.label}
                <span className="opacity-70">({chip.count})</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Canvas + detail panel */}
      <div className="flex flex-1 min-h-[520px] relative">        {error && (
          <div className="absolute top-4 left-4 right-4 z-20 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-300">
            {error}
          </div>
        )}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-khiimori-bg-secondary/50 z-10">
            <span className="text-khiimori-text-muted">Building relationship graph…</span>
          </div>
        )}
        {!loading && entities.length === 0 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-khiimori-text-muted z-10">
            <Network className="w-12 h-12 opacity-40" />
            <p className="text-sm">No tabs to map yet. Sync tabs from the extension, then click Rebuild.</p>
            <button
              type="button"
              onClick={() => void refreshGraph()}
              className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm"
            >
              Build relationship map
            </button>
          </div>
        )}
        <div
          className="absolute inset-0 cursor-grab active:cursor-grabbing bg-[radial-gradient(circle_at_1px_1px,rgba(148,163,184,0.08)_1px,transparent_0)] bg-[length:24px_24px]"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <svg
            className="w-full h-full"
            viewBox={graphViewBox}
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <filter id="node-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <g
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: 'center',
              }}
            >
              {/* Column backgrounds */}
              {columnHeaders.map((col) => {
                const colEntities = entities.filter(
                  (e) => ((e.data?._colHeader as string) ?? e.cluster) === col.label,
                );
                if (colEntities.length === 0) return null;
                const colX = (colEntities[0]?.data?._colX as number) ?? 0;
                const colW = colEntities[0]?.width ?? 210;
                const minY = Math.min(...colEntities.map((e) => e.y)) - 36;
                const maxY = Math.max(...colEntities.map((e) => e.y)) + 36;
                return (
                  <g key={col.label}>
                    <rect
                      x={colX - 12}
                      y={minY}
                      width={colW + 24}
                      height={maxY - minY}
                      rx={16}
                      fill={col.color}
                      fillOpacity={0.06}
                      stroke={col.color}
                      strokeOpacity={0.25}
                      strokeWidth={1}
                    />
                    <text
                      x={col.x}
                      y={36}
                      textAnchor="middle"
                      fontSize={13}
                      fontWeight={600}
                      fill={col.color}
                    >
                      {col.label}
                    </text>
                    <text
                      x={col.x}
                      y={52}
                      textAnchor="middle"
                      fontSize={10}
                      fill="#94a3b8"
                    >
                      {col.count} tab{col.count === 1 ? '' : 's'}
                    </text>
                  </g>
                );
              })}

              {/* Preview edge while picking a connection target */}
              {connectionMode && connectionSource && showConnections && (() => {
                const source = entities.find((e) => e.id === connectionSource);
                const targetId =
                  hoveredEntity && hoveredEntity !== connectionSource ? hoveredEntity : null;
                const target = targetId ? entities.find((e) => e.id === targetId) : null;
                if (!source || !target) return null;
                return (
                  <path
                    d={edgePath(source.x, source.y, target.x, target.y)}
                    fill="none"
                    stroke="#a78bfa"
                    strokeWidth={2.5}
                    strokeDasharray="8 5"
                    strokeOpacity={0.85}
                    pointerEvents="none"
                  />
                );
              })()}

              {/* Connections */}
              {showConnections &&
                connections.map((conn) => {
                  const source = entities.find((e) => e.id === conn.sourceId);
                  const target = entities.find((e) => e.id === conn.targetId);
                  if (!source || !target) return null;
                  if (!visibleIds.has(source.id) || !visibleIds.has(target.id)) return null;

                  const isHighlighted =
                    hoveredConnection === conn.id ||
                    (highlightedIds?.has(source.id) && highlightedIds?.has(target.id));
                  const dimmed = highlightedIds && !isHighlighted;

                  return (
                    <path
                      key={conn.id}
                      d={edgePath(source.x, source.y, target.x, target.y)}
                      fill="none"
                      stroke={getConnectionColor(conn.type, conn.label)}
                      strokeWidth={isHighlighted ? 2.5 : 1.5}
                      strokeOpacity={dimmed ? 0.12 : isHighlighted ? 0.9 : 0.45}
                      onMouseEnter={() => setHoveredConnection(conn.id)}
                      onMouseLeave={() => setHoveredConnection(null)}
                    />
                  );
                })}

              {/* Tab nodes */}
              {filteredEntities.map((entity) => {
                const w = entity.width ?? 210;
                const h = entity.height ?? 54;
                const isConnectionSource = connectionSource === entity.id;
                const isSelected = selectedEntity?.id === entity.id && !connectionMode;
                const isHovered = hoveredEntity === entity.id;
                const isHighlighted = highlightedIds?.has(entity.id);
                const dimmed =
                  !connectionMode && highlightedIds && !isHighlighted && !isSelected;
                const strokeColor = isConnectionSource ? '#fbbf24' : entity.color;

                return (
                  <g
                    key={entity.id}
                    transform={`translate(${entity.x}, ${entity.y})`}
                    onMouseEnter={() => setHoveredEntity(entity.id)}
                    onMouseLeave={() => setHoveredEntity(null)}
                    className="cursor-pointer"
                    opacity={dimmed ? 0.35 : 1}
                    filter={isSelected || isConnectionSource || isHovered ? 'url(#node-glow)' : undefined}
                  >
                    <rect
                      x={-w / 2}
                      y={-h / 2}
                      width={w}
                      height={h}
                      rx={12}
                      fill="#16131f"
                      stroke={strokeColor}
                      strokeWidth={isConnectionSource ? 3 : isSelected ? 2.5 : 1.5}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => handleEntityClick(entity, e)}
                    />
                    <circle cx={-w / 2 + 22} cy={0} r={15} fill={entity.color} fillOpacity={0.9} pointerEvents="none" />
                    <text
                      x={-w / 2 + 22}
                      y={4}
                      textAnchor="middle"
                      fontSize={11}
                      fontWeight={700}
                      fill="#0f172a"
                      pointerEvents="none"
                    >
                      T
                    </text>
                    <text
                      x={-w / 2 + 44}
                      y={-5}
                      fontSize={11}
                      fontWeight={600}
                      fill="#f1f5f9"
                      pointerEvents="none"
                    >
                      {truncateLabel(entity.name, 30)}
                    </text>
                    <text
                      x={-w / 2 + 44}
                      y={11}
                      fontSize={9}
                      fill={entity.color}
                      pointerEvents="none"
                    >
                      {entity.host || entity.cluster}
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>
        </div>
        {/* Connection legend */}
        {entities.length > 0 && (
          <div className="absolute bottom-4 right-4 z-20 rounded-xl border border-khiimori-border bg-khiimori-bg-elevated/95 backdrop-blur-sm p-3 text-xs space-y-2 max-w-[200px]">
            <p className="font-medium text-khiimori-text-secondary">Connection types</p>
            {connectionLegend.map((item) => (
              <div key={item.type} className="flex items-center gap-2 text-khiimori-text-muted">
                <span className="w-6 h-0.5 rounded" style={{ backgroundColor: item.color }} />
                {item.label}
              </div>
            ))}
          </div>
        )}

        {/* Connection mode indicator */}
        {connectionMode && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-primary-500 text-white px-4 py-2 rounded-lg flex items-center gap-2">
            <Link2 size={16} />
            {connectionSource ? 'Click another entity to connect' : 'Click an entity to start connection'}
            <button onClick={exitConnectionMode} className="ml-2">
              <X size={14} />
            </button>
          </div>
        )}

        {/* Stats overlay */}
        <div className="absolute bottom-4 left-4 z-20 rounded-xl border border-khiimori-border bg-khiimori-bg-elevated/95 backdrop-blur-sm px-3 py-2 text-xs text-khiimori-text-muted">
          {filteredEntities.length} of {entities.length} tabs shown
        </div>

        {/* Entity detail panel */}
        <AnimatePresence>
          {selectedEntity && (
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              className="absolute inset-y-0 right-0 w-80 z-40 border-l border-khiimori-border bg-khiimori-bg-secondary shadow-2xl flex flex-col"
            >
              <div className="p-4 border-b border-khiimori-border flex items-center justify-between shrink-0">
                <div>
                  <h3 className="font-semibold text-khiimori-text-primary">Tab details</h3>
                  <span className="text-xs text-khiimori-text-muted capitalize">{selectedEntity.cluster}</span>
                </div>
                <button
                  onClick={() => setSelectedEntity(null)}
                  className="p-2 hover:bg-khiimori-bg-elevated rounded-lg transition-colors"
                >
                  <X size={18} className="text-khiimori-text-muted" />
                </button>
              </div>

              <div className="p-4 flex-1 overflow-auto space-y-4">
                <div
                  className="rounded-xl p-4 border"
                  style={{ borderColor: `${selectedEntity.color}55`, backgroundColor: `${selectedEntity.color}12` }}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center text-sm font-bold text-slate-900"
                      style={{ backgroundColor: selectedEntity.color }}
                    >
                      T
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-khiimori-text-primary text-sm leading-snug">
                        {selectedEntity.name}
                      </h4>
                      {selectedEntity.host && (
                        <p className="text-xs mt-1 flex items-center gap-1" style={{ color: selectedEntity.color }}>
                          <Globe size={11} />
                          {selectedEntity.host}
                        </p>
                      )}
                    </div>
                  </div>
                  {typeof selectedEntity.data?.url === 'string' && (
                    <a
                      href={selectedEntity.data.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1 text-xs text-violet-400 hover:text-violet-300"
                    >
                      Open tab <ExternalLink size={11} />
                    </a>
                  )}
                </div>

                <div className="bg-khiimori-bg-elevated rounded-xl p-4 border border-khiimori-border">
                  <h4 className="text-sm font-medium text-khiimori-text-secondary mb-3">
                    Connected tabs (
                    {connections.filter((c) => c.sourceId === selectedEntity.id || c.targetId === selectedEntity.id).length})
                  </h4>
                  <div className="space-y-2">
                    {connections
                      .filter((c) => c.sourceId === selectedEntity.id || c.targetId === selectedEntity.id)
                      .map((conn) => {
                        const connectedId = conn.sourceId === selectedEntity.id ? conn.targetId : conn.sourceId;
                        const connected = entities.find((e) => e.id === connectedId);
                        if (!connected) return null;
                        return (
                          <button
                            key={conn.id}
                            type="button"
                            onClick={() => setSelectedEntity(connected)}
                            className="w-full flex items-center justify-between p-2.5 bg-khiimori-bg-tertiary/60 hover:bg-khiimori-bg-tertiary rounded-lg text-left transition-colors"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <div
                                className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-[10px] font-bold text-slate-900"
                                style={{ backgroundColor: connected.color }}
                              >
                                T
                              </div>
                              <span className="text-sm text-khiimori-text-secondary truncate">
                                {truncateLabel(connected.name, 32)}
                              </span>
                            </div>
                            <span
                              className="text-[10px] shrink-0 ml-2 px-1.5 py-0.5 rounded"
                              style={{
                                color: getConnectionColor(conn.type, conn.label),
                                backgroundColor: `${getConnectionColor(conn.type, conn.label)}22`,
                              }}
                            >
                              {conn.label ?? conn.type}
                            </span>
                          </button>
                        );
                      })}
                    {connections.filter((c) => c.sourceId === selectedEntity.id || c.targetId === selectedEntity.id).length === 0 && (
                      <p className="text-xs text-khiimori-text-muted">No connections yet for this tab.</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-khiimori-border shrink-0">
                <button
                  onClick={() => deleteEntity(selectedEntity.id)}
                  className="w-full py-2 bg-error-500/15 text-error-400 rounded-lg hover:bg-error-500/25 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Trash2 size={14} />
                  Remove from map
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};